import { computed, inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, of, tap } from 'rxjs';

import {
  CreateSupportTagDto,
  StringApiResponse,
  SupportTagResponseDtoApiResponse,
  SupportTagResponseDtoListApiResponse,
  SupportTagsApiService,
} from 'api';

import { AuthService } from '@auth/services/auth.service';
import { readResource } from '@core/utils';

@Injectable({ providedIn: 'root' })
export class SupportTagsService {
  private readonly api = inject(SupportTagsApiService);
  private readonly authService = inject(AuthService);

  private readonly tenantId = computed(() => this.authService.user()?.tenantId ?? null);

  private readonly tagsRs = rxResource({
    params: () => ({ tenantId: this.tenantId() }),
    stream: ({ params }) => {
      if (!params.tenantId) return of(null);
      return this.api.apiV1SupportTagsGet({ tenantId: params.tenantId });
    },
  });

  public readonly tags = readResource(this.tagsRs, (v) => v?.data ?? [], []);
  public readonly isLoading = computed(() => this.tagsRs.isLoading());
  public readonly reload = () => this.tagsRs.reload();

  public create(dto: CreateSupportTagDto): Observable<SupportTagResponseDtoApiResponse> {
    return this.api.apiV1SupportTagsPost({ createSupportTagDto: dto }).pipe(tap(() => this.reload()));
  }

  public update(id: string, dto: CreateSupportTagDto): Observable<SupportTagResponseDtoApiResponse> {
    return this.api.apiV1SupportTagsIdPut({ id, createSupportTagDto: dto }).pipe(tap(() => this.reload()));
  }

  /**
   * @param tenantIdOverride tenant al que pertenece la etiqueta; por defecto el autenticado.
   *   Lo usa el panel admin, que gestiona etiquetas de cualquier empresa.
   */
  public delete(id: string, tenantIdOverride?: string): Observable<StringApiResponse> {
    const tenantId = tenantIdOverride ?? this.tenantId();
    return this.api.apiV1SupportTagsIdDelete({ id, tenantId: tenantId ?? undefined }).pipe(tap(() => this.reload()));
  }

  /**
   * Etiquetas de un tenant **arbitrario** (panel admin). La señal `tags` solo cubre el
   * tenant autenticado.
   */
  public getByTenant(tenantId: string): Observable<SupportTagResponseDtoListApiResponse> {
    return this.api.apiV1SupportTagsGet({ tenantId });
  }
}
